import { useSignMessage, useSignTransaction } from '@privy-io/react-auth/solana';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicClient, useSwitchChain, useWriteContract } from 'wagmi';

import { useIsSmartWallet } from '@/hooks/useIsSmartWallet';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import {
  addTransactionSelector,
  recentTransactionSelector,
  useTransactionStore,
} from '@/store/transaction';

import type { ExecutionContext, ExecutionDeps } from './types';

/** The wallets, signers, clients, and store writers of an `ExecutionContext` that do not depend on the trade being submitted. */
export function useExecutionEnvironment(chainId: number | undefined) {
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const { signMessage: signSolanaMessage } = useSignMessage();
  const { signTransaction: signSolanaTransaction } = useSignTransaction();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient({ chainId });
  const isSmartWallet = useIsSmartWallet({ chainId });
  const addTransaction = useTransactionStore(addTransactionSelector);
  const { setRecentTransaction } = useTransactionStore(recentTransactionSelector);
  const queryClient = useQueryClient();

  const environment: Pick<
    ExecutionContext,
    | 'ethereumWallet'
    | 'solanaWallet'
    | 'signSolanaMessage'
    | 'signSolanaTransaction'
    | 'switchChainAsync'
    | 'publicClient'
    | 'isSmartWallet'
  > = {
    ethereumWallet,
    solanaWallet,
    signSolanaMessage,
    signSolanaTransaction,
    switchChainAsync,
    publicClient,
    isSmartWallet,
  };
  const deps: Pick<ExecutionDeps, 'addTransaction' | 'setRecentTransaction' | 'invalidateQueries'> =
    {
      addTransaction,
      setRecentTransaction,
      invalidateQueries: (key) => {
        void queryClient.invalidateQueries({ queryKey: key });
      },
    };

  return { environment, deps, writeContractAsync, queryClient };
}
